import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import { getBriefing } from '../api/speechAPI';
import type { AudioBriefingRequest } from '../types/audioRequestType';
import type { ActiveAudioContent } from '../types/activeAudioContent';
import type { CustomizationType } from '../types/customizationTypes';
import type { AdditionalResource } from '../types/additionalResource';
import { CustomTone, CustomLength } from '../types/customizationTypes';
import { getConfluenceContentAri } from '../utils/getConfluenceContentAri';

export type LastGeneratedBriefingConfig = {
	additionalResources: AdditionalResource[];
	customization: CustomizationType;
};
export type AudioBriefingState = {
	activeContent?: ActiveAudioContent;
	customization: CustomizationType;
	additionalResources: AdditionalResource[];
	cloudId: string;
	activationId: string;
	locale: string;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	hasFetched: boolean;
	lastGeneratedBriefingConfig?: LastGeneratedBriefingConfig;
	playbackSpeed: number;
	error?: Error;
	stream?: ReadableStream<Uint8Array>;
	streamEnded: boolean;
};

export type AudioBriefingInitialStateInput = Required<
	Pick<AudioBriefingState, 'activeContent' | 'cloudId' | 'activationId' | 'locale'>
>;

const DEFAULT_CUSTOMIZATION: CustomizationType = {
	tone: CustomTone.PROFESSIONAL,
	length: CustomLength.SHORT,
};

const DEFAULT_PLAYBACK_SPEED = 1;

const safeParseJson = (json: string) => {
	try {
		return JSON.parse(json);
	} catch (e) {
		return DEFAULT_CUSTOMIZATION;
	}
};

const initialAudioBriefingState: AudioBriefingState = {
	activeContent: undefined,
	customization: DEFAULT_CUSTOMIZATION,
	additionalResources: [],
	cloudId: '',
	activationId: '',
	locale: 'en-US',
	isLoading: false,
	isPlaying: false,
	isClosed: false,
	isEnded: false,
	hasFetched: false,
	lastGeneratedBriefingConfig: undefined,
	playbackSpeed: DEFAULT_PLAYBACK_SPEED,
	stream: undefined,
	error: undefined,
	streamEnded: false,
};

type AudioBriefingContainerProps = {
	initialAudioBriefingState: AudioBriefingInitialStateInput;
};

type AudioBriefingActions = {
	initialize: (
		initialState: AudioBriefingInitialStateInput,
	) => Action<AudioBriefingState, void, void>;
	addAdditionalResource: (
		newAdditionalResource: AdditionalResource,
	) => Action<AudioBriefingState, void, void>;
	removeAdditionalResource: (resourceToRemove: string) => Action<AudioBriefingState, void, void>;
	reset: () => Action<AudioBriefingState, void, void>;
	fetchBriefing: () => Action<AudioBriefingState, void, void>;
	onLoadStart: () => Action<AudioBriefingState, void, void>;
	onLoadEnd: () => Action<AudioBriefingState, void, void>;
	pauseContent: () => Action<AudioBriefingState, void, void>;
	resumeContent: () => Action<AudioBriefingState, void, void>;
	setContentPlaybackSpeed: (speed: number) => Action<AudioBriefingState, void, void>;
	stopContent: () => Action<AudioBriefingState, void, void>;
	endContent: () => Action<AudioBriefingState, void, void>;
	setContentError: (error: Error) => Action<AudioBriefingState, void, void>;
	setStreamEnded: (streamEnded: boolean) => Action<AudioBriefingState, void, void>;
	setCustomization: (
		customization: Partial<CustomizationType>,
	) => Action<AudioBriefingState, void, void>;
};

export const audioBriefingActions: AudioBriefingActions = {
	initialize:
		(initialState: AudioBriefingInitialStateInput) =>
		async ({ setState }) => {
			const storedCustomization = localStorage.getItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
			);
			const parsedCustomization = storedCustomization
				? safeParseJson(storedCustomization)
				: DEFAULT_CUSTOMIZATION;

			const emptyAudioBriefingState: AudioBriefingState = {
				stream: undefined,
				streamEnded: false,
				activeContent: undefined,
				customization: parsedCustomization,
				additionalResources: [],
				locale: 'en-US',
				cloudId: '',
				activationId: '',
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				hasFetched: false,
				error: undefined,
				playbackSpeed:
					Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) ||
					DEFAULT_PLAYBACK_SPEED,
			};
			setState(Object.assign(emptyAudioBriefingState, initialState));
		},
	reset:
		() =>
		({ setState }) => {
			setState(initialAudioBriefingState);
		},
	addAdditionalResource:
		(newAdditionalResource: AdditionalResource) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			setState({
				additionalResources: [...additionalResources, newAdditionalResource],
			});
		},
	removeAdditionalResource:
		(ariToRemove: string) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			const newResourcesList = additionalResources.filter(
				(resource) => resource.ari !== ariToRemove,
			);
			setState({
				additionalResources: newResourcesList,
			});
		},
	fetchBriefing:
		() =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioBriefingActions.onLoadStart());

			const { activeContent, additionalResources, cloudId, activationId, locale, customization } =
				getState();

			if (!activeContent) {
				dispatch(
					audioBriefingActions.setContentError(
						new Error('Briefing container needs to be initialized'),
					),
				);
				return;
			}

			const activeContentAri = getConfluenceContentAri(
				cloudId,
				activeContent.id,
				activeContent.contentType,
			);

			if (!activeContentAri) {
				dispatch(audioBriefingActions.setContentError(new Error('Content ARI failed to generate')));
				return;
			}

			const audioRequest: AudioBriefingRequest = {
				activeContentAri,
				additionalResources,
				activationId,
				locale,
				customization,
			};

			try {
				const stream = await getBriefing(audioRequest);

				if (getState().isClosed) {
					return;
				}

				setState({
					isPlaying: false,
					isEnded: false,
					stream,
					lastGeneratedBriefingConfig: {
						additionalResources,
						customization,
					},
				});
			} catch (e) {
				setState({
					error: e,
					stream: undefined,
				});
			} finally {
				dispatch(audioBriefingActions.onLoadEnd());
			}
		},
	onLoadStart:
		() =>
		({ setState }) => {
			setState({
				isLoading: true,
				hasFetched: true,
				error: undefined,
				streamEnded: false,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	pauseContent:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	resumeContent:
		() =>
		({ setState, getState }) => {
			const { stream } = getState();
			if (!stream) {
				throw new Error('Must fetch before playing');
			}
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	setContentPlaybackSpeed:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	stopContent:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioBriefingActions.reset());
			setState({ isClosed: true });
		},
	endContent:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				isPlaying: false,
			});
		},
	setContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
			});
		},
	setStreamEnded:
		(streamEnded: boolean) =>
		({ setState }) => {
			setState({
				streamEnded,
			});
		},
	setCustomization:
		(newCustomization: Partial<CustomizationType>) =>
		({ setState, getState }) => {
			const { customization } = getState();
			const updatedCustomization = { ...customization, ...newCustomization };

			localStorage.setItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
				JSON.stringify({
					tone: updatedCustomization.tone,
					length: updatedCustomization.length,
				}),
			);
			setState({ customization: updatedCustomization });
		},
};

// Exported for tests
export const Store = createStore<AudioBriefingState, AudioBriefingActions>({
	initialState: {
		activeContent: undefined,
		customization: DEFAULT_CUSTOMIZATION,
		additionalResources: [],
		cloudId: '',
		activationId: '',
		locale: 'en-US',
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		hasFetched: false,
		playbackSpeed: 1,
		error: undefined,
		streamEnded: false,
	},
	actions: audioBriefingActions,
	name: 'listenerState',
});

export const AudioBriefingContainer = createContainer<
	AudioBriefingState,
	AudioBriefingActions,
	AudioBriefingContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioBriefingState: initialAudioPlaybackState }) => {
			void dispatch(audioBriefingActions.initialize(initialAudioPlaybackState));
		},
});

export const useAudioBriefing = createHook<AudioBriefingState, AudioBriefingActions>(Store);
export const useAudioBriefingActions = createActionsHook(Store);
